/**
 * 認証ポリシー（設定 > 認証 > 認証ポリシー）の Contract。**publish 前のローカル定義**。
 *
 * dresscode-backend #8514 の
 * `shared-kernel/api/settings/usecase/authentication-policy.api-contract.ts` を写したもの。
 * publish 後はこのファイルを削除し `@standandforce/api-contract` からの import に差し替える
 * （差し替えを import 変更だけで済ませるため、BE のファイル分割・スキーマ名をそのまま保つ）。
 *
 * JSON の意味論（全域性・DENY の obligation 禁止等）の検証はドメイン層が唯一の正で、
 * create / update / validate / preview のすべてが同じロジックを通る。
 *
 * **デプロイ順**: BE #8514 を先に出すこと。この Contract は葉条件の `negated` 必須、
 * ルールの `id` / `otherwiseOutcome`、outcome の `requirementPreset` / `maxAuthAgeMinutes`、
 * ValidationIssue の `severity`、Summary の `reachable` / locale 分岐、`preview` を前提にしており、
 * 旧 BE のレスポンスは output パースに失敗して詳細画面が表示できない。
 *
 * size limits はローカルでは簡略化（フィールド名・null 意味論は BE と一致）。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { pageSizeAllowingAllSchema, paginatedSchema, paginationQuerySchema } from './pagination';
export const AuthenticationPolicyStatusSchema = z.enum(['DRAFT', 'REPORT_ONLY', 'ENFORCED']);
export const AuthenticationPolicyConditionJoinSchema = z.enum(['AND', 'OR']);
const PersonalEmailLoginModeEnum = z.enum(['DISALLOWED', 'UNTIL_WORK_EMAIL_SET', 'ALWAYS_ALLOWED']);

/** Ory の Authenticator Assurance Level（aal2 = MFA 済み）。 */
const AalEnum = z.enum(['aal1', 'aal2']);
const LoginIdentifierEnum = z.enum(['work', 'personal']);

/** 許可ログイン方式・USED_LOGIN_METHOD 条件で共有する方式選択。 */
export const AuthenticationPolicyLoginMethodSelectionSchema = z.object({
  includesPassword: z.boolean(),
  /** federated（SSO）方式として許可する SSO 接続 ID */
  ssoConnectionIds: z.array(z.string().min(1))
});

/**
 * 認証強度プリセット。定義（表示名・展開形）の SSoT は BE ドメイン。
 * 入力で指定時、`authenticationRequirement` はサーバ導出（クライアント値は無視）。
 */
export const AuthenticationRequirementPresetSchema = z.enum(['STANDARD_MFA', 'STRONG_AUTH']);
const leafConditionOptions = [z.object({
  kind: z.literal('NETWORK_IN'),
  /** it-network 台帳の Network ID */
  networkIds: z.array(z.string().min(1)).min(1),
  /** false = に含まれる / true = 含まれない */
  negated: z.boolean()
}), z.object({
  kind: z.literal('DEVICE_IN_GROUPS'),
  /** it-device 台帳の DeviceGroup ID */
  deviceGroupIds: z.array(z.string().min(1)).min(1),
  /** false = に含まれる / true = 含まれない */
  negated: z.boolean()
}), z.object({
  kind: z.literal('SESSION_IS_FEDERATED'),
  /** false = である / true = でない */
  negated: z.boolean()
}), z.object({
  kind: z.literal('SESSION_AAL_AT_LEAST'),
  level: AalEnum,
  /** false = 以上である / true = 以上でない（未満） */
  negated: z.boolean()
}), z.object({
  kind: z.literal('USED_LOGIN_METHOD'),
  includesPassword: z.boolean(),
  ssoConnectionIds: z.array(z.string().min(1)),
  /** false = いずれかである / true = いずれでもない */
  negated: z.boolean()
}), z.object({
  kind: z.literal('LOGIN_IDENTIFIER'),
  identifier: LoginIdentifierEnum,
  /** false = である / true = でない */
  negated: z.boolean()
}), z.object({
  kind: z.literal('IDENTITY_MFA_ENROLLED'),
  /** false = MFA 登録済み / true = MFA 未登録 */
  negated: z.boolean()
})] as const;
export const AuthenticationPolicyLeafConditionSchema = z.discriminatedUnion('kind', [...leafConditionOptions]);

/** ルール直下の条件ノード（葉 or 1 段グループ。グループは葉のみを持つ）。 */
export const AuthenticationPolicyConditionNodeSchema = z.discriminatedUnion('kind', [...leafConditionOptions, z.object({
  kind: z.literal('GROUP'),
  join: AuthenticationPolicyConditionJoinSchema,
  conditions: z.array(AuthenticationPolicyLeafConditionSchema).min(1)
})]);
export const AuthenticationRequirementElementSchema = z.discriminatedUnion('kind', [z.object({
  kind: z.literal('FEDERATED_SESSION')
}), z.object({
  kind: z.literal('AAL_AT_LEAST'),
  level: AalEnum
})]);

/**
 * ルールの outcome。各 obligation の `null` = 制限なし。
 * DENY は obligation を持てない。
 * 発効日時はルール単位ではなくポリシー単位（`AuthenticationPolicyDraftSchema.enforceFrom`）。
 */
export const AuthenticationPolicyOutcomeSchema = z.object({
  decision: z.enum(['ALLOW', 'DENY']),
  /** 認証強度プリセット。null = プリセット未使用（カスタム構成 or 強度要求なし） */
  requirementPreset: AuthenticationRequirementPresetSchema.nullable(),
  /**
   * 認証強度（anyOf: いずれか 1 つ満たせば充足）。
   * `requirementPreset` 指定時はサーバ導出（入力値は無視され、展開形で上書きされる）
   */
  authenticationRequirement: z.array(AuthenticationRequirementElementSchema).nullable(),
  allowedLoginMethods: AuthenticationPolicyLoginMethodSelectionSchema.nullable(),
  personalEmailMode: PersonalEmailLoginModeEnum.nullable(),
  /** セッション最大認証経過時間（分）。null = 制限なし。DENY は持てない */
  maxAuthAgeMinutes: z.number().int().min(1).max(10080).nullable()
});
export const AuthenticationPolicyRuleInputSchema = z.object({
  /**
   * 既存ルールの ID（null/省略 = 新規採番）。
   * 更新時に GET した id を送り返すと監査用の ruleId が保持される。
   */
  id: z.string().nullable().optional(),
  /** 表示名（UI のルール見出し）。null = 無題 */
  name: z.string().nullable(),
  conditionJoin: AuthenticationPolicyConditionJoinSchema,
  /**
   * 空 = 無条件（catch-all）。
   * 全域ルール = conditions.length === 0 または otherwiseOutcome != null。
   * 最終ルールは全域でなければならない（PolicyMustBeTotal）。
   */
  conditions: z.array(AuthenticationPolicyConditionNodeSchema),
  outcome: AuthenticationPolicyOutcomeSchema,
  /**
   * 条件不一致時に適用する outcome。
   * null = 次のルールへ（EVALUATE_NEXT）。
   * 条件なし（catch-all）ルールでは常に null（OtherwiseOnCatchAllForbidden）。
   * デフォルトポリシーでは常に null（DefaultPolicyMustNotHaveOtherwise）。
   */
  otherwiseOutcome: AuthenticationPolicyOutcomeSchema.nullable()
});
export const AuthenticationPolicyRuleViewSchema = AuthenticationPolicyRuleInputSchema.extend({
  id: z.string()
});

/** create / update / validate / preview 共通の下書き形状。priority は作成時に自動採番される。 */
export const AuthenticationPolicyDraftSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  status: AuthenticationPolicyStatusSchema,
  /**
   * ポリシー単位の発効日時（オフセット付き ISO8601）。null = 即時有効。
   * 到達前は評価候補に入らず、後続の適用可能ポリシー / デフォルトへフォールスルーする。
   * デフォルトポリシーは設定不可。
   */
  enforceFrom: z.string().datetime({
    offset: true
  }).nullable(),
  /**
   * ruleOrder は配列順。
   * 最終要素は全域（条件なし、または otherwiseOutcome 付き）であること。
   */
  rules: z.array(AuthenticationPolicyRuleInputSchema).min(1)
});
export const AuthenticationPolicySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /**
   * 内部の評価順の値（小さいほど優先。並び替えは値の付け替えのため飛び番あり）。
   * UI は序数として表示せず、一覧の並び順（= 評価順）を表示に使うこと。
   */
  priority: z.number().int(),
  isDefault: z.boolean(),
  status: AuthenticationPolicyStatusSchema,
  /** ポリシー単位の発効日時。null = 即時有効（一覧の「◯日から有効」バッジ用） */
  enforceFrom: z.string().datetime().nullable(),
  ruleCount: z.number().int(),
  /** このポリシーを参照するアクセスルール数（UI の「適用数」） */
  attachmentCount: z.number().int(),
  /**
   * 一覧向けの主な認証要件の要約（例: 「社内=許可 / 社外=強認証」）。
   * BE 未対応時は欠落・null になり得る。
   */
  requirementSummary: z.string().nullable().optional(),
  updatedAt: z.string().datetime()
});
export const AuthenticationPolicyDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** 内部の評価順の値。序数表示には使わない（AuthenticationPolicySummarySchema.priority 参照） */
  priority: z.number().int(),
  isDefault: z.boolean(),
  status: AuthenticationPolicyStatusSchema,
  /** ポリシー単位の発効日時。null = 即時有効 */
  enforceFrom: z.string().datetime().nullable(),
  rules: z.array(AuthenticationPolicyRuleViewSchema),
  attachmentCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
const DisplayMessagesSchema = z.object({
  en: z.string(),
  ja: z.string(),
  id: z.string(),
  vn: z.string(),
  th: z.string(),
  zhCN: z.string()
});
export const AuthenticationPolicyValidationIssueSchema = z.object({
  /** 指摘位置（例: `rules[1].conditions[0]`）。ポリシー全体への指摘は null */
  path: z.string().nullable(),
  /** エラー種別の安定コード（例: `PolicyMustBeTotal`） */
  code: z.string(),
  /** ERROR = Entity 組み立て不可。WARNING = 注意喚起（valid には影響しない） */
  severity: z.enum(['ERROR', 'WARNING']),
  messages: DisplayMessagesSchema
});
export const AuthenticationPolicyValidateResultSchema = z.object({
  /**
   * ERROR が 0 件なら true。
   * WARNING のみでも valid（保存可）。
   */
  valid: z.boolean(),
  issues: z.array(AuthenticationPolicyValidationIssueSchema)
});

/** 更新対象の既存ポリシーを指定すると、その isDefault / 名前重複を含めて検証する。 */
export const AuthenticationPolicyValidateInputSchema = AuthenticationPolicyDraftSchema.extend({
  id: z.string().nullable().optional()
});

/**
 * ルールの UI バナー要約に使うアクション分類。
 * - `ALLOW` / `DENY`: 許可 / 拒否
 * - `STEP_UP`: 追加認証を要求
 * - `EVALUATE_NEXT`: 次のルールで評価
 */
export const AuthenticationPolicySummaryActionSchema = z.enum(['ALLOW', 'STEP_UP', 'DENY', 'EVALUATE_NEXT']);
export const AuthenticationPolicySummarySegmentSchema = z.discriminatedUnion('kind', [z.object({
  kind: z.literal('TEXT'),
  text: z.string()
}), z.object({
  kind: z.literal('ACTION'),
  action: AuthenticationPolicySummaryActionSchema,
  text: z.string()
})]);
const LocalizedSummarySegmentsSchema = z.object({
  en: z.array(AuthenticationPolicySummarySegmentSchema),
  ja: z.array(AuthenticationPolicySummarySegmentSchema),
  id: z.array(AuthenticationPolicySummarySegmentSchema),
  vn: z.array(AuthenticationPolicySummarySegmentSchema),
  th: z.array(AuthenticationPolicySummarySegmentSchema),
  zhCN: z.array(AuthenticationPolicySummarySegmentSchema)
});
const SummaryConditionLeafAllLocalesSchema = z.object({
  nodeKind: z.literal('LEAF'),
  conditionKind: z.string(),
  negated: z.boolean(),
  kindLabel: DisplayMessagesSchema,
  operatorLabel: DisplayMessagesSchema,
  referenceIds: z.array(z.string()).nullable(),
  level: z.string().nullable(),
  identifier: z.string().nullable(),
  includesPassword: z.boolean().nullable()
});
const SummaryConditionLeafSingleLocaleSchema = z.object({
  nodeKind: z.literal('LEAF'),
  conditionKind: z.string(),
  negated: z.boolean(),
  kindLabel: z.string(),
  operatorLabel: z.string(),
  referenceIds: z.array(z.string()).nullable(),
  level: z.string().nullable(),
  identifier: z.string().nullable(),
  includesPassword: z.boolean().nullable()
});

/** allLocales=true のとき返す 6 言語版ルールサマリ */
export const AuthenticationPolicyRuleSummarySchema = z.object({
  /** 入力 `rules` の index と 1:1 対応 */
  ruleIndex: z.number().int(),
  /** このルールに到達可能か。先行する total ルールの後は false */
  reachable: z.boolean(),
  /** 条件一致時（THEN）のアクション */
  matchedAction: AuthenticationPolicySummaryActionSchema,
  /**
   * 条件不一致時（OTHERWISE）のアクション。
   * - 明示 `otherwiseOutcome` がある場合はその導出アクション
   * - null（EVALUATE_NEXT）のとき常に EVALUATE_NEXT（次 catch-all の先読みはしない）
   * - catch-all ルール自身は null
   */
  otherwiseAction: AuthenticationPolicySummaryActionSchema.nullable(),
  conditionJoin: AuthenticationPolicyConditionJoinSchema,
  conditionJoinLabel: DisplayMessagesSchema,
  conditions: z.array(z.discriminatedUnion('nodeKind', [SummaryConditionLeafAllLocalesSchema, z.object({
    nodeKind: z.literal('GROUP'),
    join: AuthenticationPolicyConditionJoinSchema,
    joinLabel: DisplayMessagesSchema,
    children: z.array(SummaryConditionLeafAllLocalesSchema)
  })])),
  segments: LocalizedSummarySegmentsSchema,
  plainText: DisplayMessagesSchema,
  requirements: z.array(z.object({
    code: z.string(),
    label: DisplayMessagesSchema
  }))
});
export const SupportedLocaleSchema = z.enum(['en', 'ja', 'id', 'vn', 'th', 'zhCN']);

/** allLocales=false のとき返す単一ロケール版ルールサマリ */
export const AuthenticationPolicyRuleSummarySingleLocaleSchema = z.object({
  ruleIndex: z.number().int(),
  reachable: z.boolean(),
  matchedAction: AuthenticationPolicySummaryActionSchema,
  otherwiseAction: AuthenticationPolicySummaryActionSchema.nullable(),
  conditionJoin: AuthenticationPolicyConditionJoinSchema,
  conditionJoinLabel: z.string(),
  conditions: z.array(z.discriminatedUnion('nodeKind', [SummaryConditionLeafSingleLocaleSchema, z.object({
    nodeKind: z.literal('GROUP'),
    join: AuthenticationPolicyConditionJoinSchema,
    joinLabel: z.string(),
    children: z.array(SummaryConditionLeafSingleLocaleSchema)
  })])),
  segments: z.array(AuthenticationPolicySummarySegmentSchema),
  plainText: z.string(),
  requirements: z.array(z.object({
    code: z.string(),
    label: z.string()
  }))
});

/** 下書き（未保存）のルール列をそのまま受ける。構造の妥当性は要求しない。 */
export const AuthenticationPolicySummaryInputSchema = AuthenticationPolicyDraftSchema.pick({
  rules: true
}).extend({
  /** 単一ロケール出力時に使用する言語。デフォルト `ja` */
  locale: SupportedLocaleSchema.default('ja'),
  /** true = 6言語出力（監査・履歴用）。false = 単一ロケール出力（既定） */
  allLocales: z.boolean().default(false)
});
export const AuthenticationPolicySummaryResultSchema = z.discriminatedUnion('allLocales', [z.object({
  allLocales: z.literal(false),
  locale: SupportedLocaleSchema,
  rules: z.array(AuthenticationPolicyRuleSummarySingleLocaleSchema)
}), z.object({
  allLocales: z.literal(true),
  rules: z.array(AuthenticationPolicyRuleSummarySchema)
})]);

/**
 * validate + summary を一括実行するプレビュー入力。
 * エディタが保存前に 1 リクエストで検証結果と要約を取得する。
 */
export const AuthenticationPolicyPreviewInputSchema = AuthenticationPolicyValidateInputSchema.extend({
  locale: SupportedLocaleSchema.default('ja'),
  allLocales: z.boolean().default(false)
});
export const AuthenticationPolicyPreviewResultSchema = z.object({
  /** ERROR がなければ valid（WARNING のみなら true） */
  valid: z.boolean(),
  /** ERROR + WARNING の全指摘 */
  issues: z.array(AuthenticationPolicyValidationIssueSchema),
  summary: AuthenticationPolicySummaryResultSchema
});

/** 組織あたりの認証ポリシー数上限（BE と同値。reorder の orderedIds.max に使う）。 */
export const MAX_AUTHENTICATION_POLICIES = 200;
const listOrderFields = z.enum(['priority', 'name', 'updatedAt']);
const AuthenticationPolicyListQuerySchema = paginationQuerySchema(listOrderFields).extend({
  pageSize: pageSizeAllowingAllSchema()
});

/**
 * 並び替え入力。非デフォルトポリシー全件の ID を新しい評価順（先頭が最優先）で列挙する。
 * 部分並び替えは受け付けない。
 */
export const AuthenticationPolicyReorderInputSchema = z.object({
  orderedIds: z.array(z.string().min(1)).min(1).max(MAX_AUTHENTICATION_POLICIES)
});
export const AuthenticationPolicyReorderResultSchema = z.object({
  /** 並び替え後の全ポリシー（評価順。デフォルトは常に最後） */
  items: z.array(AuthenticationPolicySummarySchema)
});
export const authenticationPolicyContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: '/v2/settings/authentication-policies'
  }).input(AuthenticationPolicyListQuerySchema).output(paginatedSchema(AuthenticationPolicySummarySchema)),
  get: oc.route({
    method: 'GET',
    path: '/v2/settings/authentication-policies/{id}'
  }).input(z.object({
    id: z.string()
  })).output(AuthenticationPolicyDetailSchema),
  create: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies'
  }).input(AuthenticationPolicyDraftSchema).output(AuthenticationPolicyDetailSchema),
  update: oc.route({
    method: 'PUT',
    path: '/v2/settings/authentication-policies/{id}'
  }).input(AuthenticationPolicyDraftSchema.extend({
    id: z.string()
  })).output(AuthenticationPolicyDetailSchema),
  delete: oc.route({
    method: 'DELETE',
    path: '/v2/settings/authentication-policies/{id}'
  }).input(z.object({
    id: z.string()
  })).output(z.object({
    id: z.string()
  })),
  /**
   * 保存せずに下書きを検証する。create / update と同じ検証ロジック
   * （構造の不変条件 + 参照 SSO 接続の実在性）を通し、指摘を全件返す。
   */
  validate: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies/validate'
  }).input(AuthenticationPolicyValidateInputSchema).output(AuthenticationPolicyValidateResultSchema),
  /**
   * ルール列を解釈して表示用の Summary を生成する。保存不要の純関数的 API。
   * エディタからは `/preview` を推奨（validate + summary を一括取得）。
   */
  summary: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies/summary'
  }).input(AuthenticationPolicySummaryInputSchema).output(AuthenticationPolicySummaryResultSchema),
  /**
   * validate + summary を一括実行するプレビュー API。
   * エディタが保存前に 1 リクエストで検証結果と要約を取得する。
   * `/validate` と `/summary` は個別にも利用可能（preview が推奨）。
   */
  preview: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies/preview'
  }).input(AuthenticationPolicyPreviewInputSchema).output(AuthenticationPolicyPreviewResultSchema),
  /**
   * 非デフォルトポリシーの評価順（priority）を一括で並び替える。
   * デフォルトポリシーは常に最後に評価されるため対象外。
   */
  reorder: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies/reorder'
  }).input(AuthenticationPolicyReorderInputSchema).output(AuthenticationPolicyReorderResultSchema)
});
export type AuthenticationPolicyStatus = z.infer<typeof AuthenticationPolicyStatusSchema>;
export type AuthenticationPolicyConditionJoin = z.infer<typeof AuthenticationPolicyConditionJoinSchema>;
export type AuthenticatorAssuranceLevel = z.infer<typeof AalEnum>;
export type LoginIdentifierKind = z.infer<typeof LoginIdentifierEnum>;
export type PersonalEmailLoginMode = z.infer<typeof PersonalEmailLoginModeEnum>;
export type AuthenticationRequirementPreset = z.infer<typeof AuthenticationRequirementPresetSchema>;
export type AuthenticationPolicyLoginMethodSelection = z.infer<typeof AuthenticationPolicyLoginMethodSelectionSchema>;
export type AuthenticationPolicyLeafCondition = z.infer<typeof AuthenticationPolicyLeafConditionSchema>;
export type AuthenticationPolicyConditionKind = AuthenticationPolicyLeafCondition['kind'];
export type AuthenticationPolicyConditionNode = z.infer<typeof AuthenticationPolicyConditionNodeSchema>;
export type AuthenticationRequirementElement = z.infer<typeof AuthenticationRequirementElementSchema>;
export type AuthenticationPolicyOutcome = z.infer<typeof AuthenticationPolicyOutcomeSchema>;
export type AuthenticationPolicyRuleInput = z.infer<typeof AuthenticationPolicyRuleInputSchema>;
export type AuthenticationPolicyRuleView = z.infer<typeof AuthenticationPolicyRuleViewSchema>;
export type AuthenticationPolicyDraftInput = z.infer<typeof AuthenticationPolicyDraftSchema>;
export type AuthenticationPolicySummary = z.infer<typeof AuthenticationPolicySummarySchema>;
export type AuthenticationPolicyDetail = z.infer<typeof AuthenticationPolicyDetailSchema>;
export type AuthenticationPolicyValidationIssue = z.infer<typeof AuthenticationPolicyValidationIssueSchema>;
export type AuthenticationPolicyValidateResult = z.infer<typeof AuthenticationPolicyValidateResultSchema>;
export type AuthenticationPolicyValidateInput = z.infer<typeof AuthenticationPolicyValidateInputSchema>;
export type AuthenticationPolicySummaryAction = z.infer<typeof AuthenticationPolicySummaryActionSchema>;
export type AuthenticationPolicySummarySegment = z.infer<typeof AuthenticationPolicySummarySegmentSchema>;
export type AuthenticationPolicyRuleSummary = z.infer<typeof AuthenticationPolicyRuleSummarySchema>;
export type AuthenticationPolicyRuleSummarySingleLocale = z.infer<typeof AuthenticationPolicyRuleSummarySingleLocaleSchema>;
export type SupportedLocale = z.infer<typeof SupportedLocaleSchema>;
export type AuthenticationPolicySummaryInput = z.infer<typeof AuthenticationPolicySummaryInputSchema>;
export type AuthenticationPolicySummaryResult = z.infer<typeof AuthenticationPolicySummaryResultSchema>;
export type AuthenticationPolicyPreviewInput = z.infer<typeof AuthenticationPolicyPreviewInputSchema>;
export type AuthenticationPolicyPreviewResult = z.infer<typeof AuthenticationPolicyPreviewResultSchema>;
export type AuthenticationPolicyReorderInput = z.infer<typeof AuthenticationPolicyReorderInputSchema>;
export type AuthenticationPolicyReorderResult = z.infer<typeof AuthenticationPolicyReorderResultSchema>;
export type AuthenticationPolicyDisplayMessages = z.infer<typeof DisplayMessagesSchema>;
export type AuthenticationPolicyValidationSeverity = AuthenticationPolicyValidationIssue['severity'];